import { useMemo } from 'react';

import { ProductCard } from 'components/product-card';
import { IPriceDto } from 'generated/graphql-gateway';
import { useFormatCalories } from 'hooks/use-format-calories';
import { useUnifiedMenuContext } from 'state/unified-menu';
import { ILocalizedMenuEntry } from 'state/unified-menu/types';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { getEntryPrice, stringifySanityImage } from 'state/unified-menu/utils';
import { formatCalorieRange } from 'utils/menu/calories';

import { getAspectOptionMinMaxCalories } from './utils';

interface PickerAspectOptionProps {
  aspectOption: ILocalizedMenuEntry;
  product: ILocalizedMenuEntry;
  selected: boolean;
  onSelected: () => void;
  showPriceUpcharge: boolean;
  upcharge: IPriceDto;
}

export const PickerAspectOption = ({
  aspectOption,
  product,
  selected,
  onSelected,
  showPriceUpcharge,
  upcharge,
}: PickerAspectOptionProps) => {
  const menuQueryOptions = useMenuQueryOptions();
  const { indexedMenu } = useUnifiedMenuContext();
  const formatCalories = useFormatCalories();
  const aspectOptionName = sanitizeAspectOptionName(aspectOption.name?.locale ?? '');

  const caloriesRange = useMemo(() => {
    const range = getAspectOptionMinMaxCalories(
      indexedMenu,
      menuQueryOptions.posServiceMode,
      aspectOption.id
    );

    const caloriesRange = formatCalorieRange({
      min: range.min,
      max: range.max,
      formatFn: formatCalories,
      length: range.max === range.min ? 1 : Infinity,
    });

    return caloriesRange;
  }, [indexedMenu, menuQueryOptions.posServiceMode, aspectOption.id, formatCalories]);

  return (
    <ProductCard
      productProps={{
        name: aspectOptionName,
        image: aspectOption.image
          ? stringifySanityImage(aspectOption.image)
          : stringifySanityImage(product.image),
        calories: caloriesRange,
        price: showPriceUpcharge
          ? upcharge[menuQueryOptions.posServiceMode] ?? undefined
          : getEntryPrice(product, menuQueryOptions.posServiceMode),
        showPriceUpcharge,
      }}
      isSelected={selected}
      isFlippable={false}
      onProductPress={onSelected}
    />
  );
};

function sanitizeAspectOptionName(name: string | undefined): string {
  const [optionName] = (name ?? '').split('-');
  return optionName.trim();
}
